import { inject, injectable, interfaces } from 'inversify';
import Semaphore from 'semaphore-async-await';
import { delay, randomIntFromInterval } from '../../helpers/function.helpers';
import { ACDLError } from '../../models/error.model';
import { ProjectRole } from '../../models/userRole.model';
import { IMainApiService } from '../../services/api.service';
import { IApiService } from '../../services/api.service.base';
import { ProjectStatus } from '../projects/projects.model';
import { IAnnotationProgress, IImage, IReviewProgress } from './annotations.interface';
import { cleanClones, IQuestionModel } from './question.model';

export interface IAnnotationApiService {
  BaseUrl: string;
  getImageForAnnotationAsync(projectId: string, unlockOldImages: boolean): Promise<IImage | ACDLError>;
  requestQuestionsAsync(projectId: string): Promise<IQuestionModel[] | ACDLError>;
  unlockImageAsync(imageId: string, projectId: string, skip: boolean): Promise<void | ACDLError>;
  unlockUserImagesForProjectAsync(projectId: string): Promise<void | ACDLError>;
  requestImageToReviewAsync(projectId: string, unlockOldImages: boolean): Promise<IImage | ACDLError>;
  reLockImageForAnnotationAsync(imageId: string, projectId: string): Promise<void | ACDLError>;
  reLockImageForReviewAsync(imageId: string, projectId: string): Promise<void | ACDLError>;
  getAnnotationProgressAsync(projectId: string): Promise<IAnnotationProgress | ACDLError>;
  getReviewProgressAsync(projectId: string): Promise<IReviewProgress | ACDLError>;
  getProjectAnnotationInfoAsync(projectId: string): Promise<IGetProjectAnnotationInfoResponse>;
}

export namespace IAnnotationApiService {
  export const $: interfaces.ServiceIdentifier<IAnnotationApiService> = Symbol('ANNOTATION_API_SERVICE');
}

interface IGetProjectAnnotationInfoResponse {
  projectStatus: ProjectStatus;
  projectRole: ProjectRole;
}

@injectable()
export class AnnotationApiService implements IAnnotationApiService {
  constructor(@inject(IMainApiService.$) private readonly apiService: IApiService) {}

  get BaseUrl(): string {
    return this.apiService.config.baseUrl;
  }

  private annotationLock = new Semaphore(1);
  private reviewLock = new Semaphore(1);

  async requestQuestionsAsync(projectId: string): Promise<IQuestionModel[] | ACDLError> {
    const result = await this.apiService.getAsync<IQuestionModel[]>(`/questions?projectId=${projectId}`);
    if (result instanceof ACDLError) {
      return result;
    }
    return cleanClones(result);
  }

  async getImageForAnnotationAsync(projectId: string, removeOldLocks: boolean): Promise<IImage | ACDLError> {
    await this.annotationLock.acquire();
    let isConcurrencyError = false;
    let circuitBreaker = 50;
    let result: IImage | ACDLError | undefined = undefined;

    do {
      isConcurrencyError = false;
      // eslint-disable-next-line no-await-in-loop -- retry
      result = await this.apiService.postAsync<IImage | ACDLError>('/Images/GetImageForAnnotation', {
        removeOldLocks,
        projectId,
      });
      if (result instanceof ACDLError) {
        isConcurrencyError = result.apiErrorResponse?.statusCode === 409;
        circuitBreaker -= 1;
        if (circuitBreaker && isConcurrencyError) {
          // eslint-disable-next-line no-await-in-loop -- wait in retry
          await delay(randomIntFromInterval(100, 500));
        }
      }
    } while (isConcurrencyError && circuitBreaker);

    this.annotationLock.release();
    return result;
  }

  unlockImageAsync = async (imageId: string, projectId: string, skip: boolean): Promise<void | ACDLError> =>
    this.apiService.postAsync('/Images/UnlockImageForProject', { imageId, projectId, skip });

  unlockUserImagesForProjectAsync = async (projectId: string): Promise<void | ACDLError> =>
    this.apiService.postAsync('/Images/UnlockUserImagesForProject', { projectId });

  async requestImageToReviewAsync(projectId: string, removeOldLocks: boolean): Promise<IImage | ACDLError> {
    await this.reviewLock.acquire();
    let isConcurrencyError = false;
    let circuitBreaker = 50;
    let result: IImage | ACDLError | undefined = undefined;

    do {
      isConcurrencyError = false;
      // eslint-disable-next-line no-await-in-loop -- retry
      result = await this.apiService.postAsync<IImage | ACDLError>('/Images/GetImageToReview', {
        projectId,
        removeOldLocks,
      });
      if (result instanceof ACDLError) {
        isConcurrencyError = result.apiErrorResponse?.statusCode === 409;
        circuitBreaker -= 1;
        if (circuitBreaker && isConcurrencyError) {
          // eslint-disable-next-line no-await-in-loop -- wait in retry
          await delay(randomIntFromInterval(100, 500));
        }
      }
    } while (isConcurrencyError && circuitBreaker);

    this.reviewLock.release();
    return result;
  }

  reLockImageForAnnotationAsync = async (imageId: string, projectId: string): Promise<void | ACDLError> =>
    this.apiService.postAsync('/images/ReLockImageForAnnotation', { imageId, projectId });

  reLockImageForReviewAsync = async (imageId: string, projectId: string): Promise<void | ACDLError> =>
    this.apiService.postAsync('/images/ReLockImageForReview', { imageId, projectId });

  getAnnotationProgressAsync = async (projectId: string): Promise<IAnnotationProgress | ACDLError> =>
    this.apiService.getAsync<IAnnotationProgress>(`/projects/getAnnotationProgress?projectId=${projectId}`);

  getReviewProgressAsync = async (projectId: string): Promise<IReviewProgress | ACDLError> =>
    this.apiService.getAsync<IReviewProgress>(`/projects/getReviewProgress?projectId=${projectId}`);

  getProjectAnnotationInfoAsync = async (projectId: string): Promise<IGetProjectAnnotationInfoResponse> => {
    const result = await this.apiService.getAsync<IGetProjectAnnotationInfoResponse>(`/projects/getProjectStatus?projectId=${projectId}`);
    if (result instanceof ACDLError) {
      throw result;
    }

    return result;
  };
}
